import { useEffect, useRef, useState } from "react";
import { api, fmtPct, fmtUsd, fmtNum, retryAfterOf } from "@/lib/api";
import { CoinRow } from "@/components/CoinRow";
import { RetryButton } from "@/components/coin/MarketDataNotice";
import { Term } from "@/components/Term";
import { TrendingUp, TrendingDown, Flame, Gauge, Globe, Star } from "lucide-react";
import { DashboardGauges } from "@/components/dashboard/DashboardGauges";
import { useNavigate } from "react-router-dom";
import { Hint, HintLabel } from "@/components/Hint";
import { useLivePrices } from "@/hooks/useLivePrices";
import { PriceTicker } from "@/components/PriceTicker";

const Stat = ({ k, label, value, hint, accent }) => (
  <div className="apx-card p-4 flex flex-col gap-1 rise">
    <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500"><HintLabel k={k}>{label}</HintLabel></div>
    <div className={`font-mono-ticker text-lg md:text-xl font-semibold ${accent || "text-slate-100"}`}>{value}</div>
    {hint && <div className="text-xs text-slate-500">{hint}</div>}
  </div>
);

const LiveBadge = ({ n, tid }) => n > 0 ? (
  <Hint title="Live prices" text="Prices in this grid tick in real time from the EV-IDEX market feed — no refresh needed." asChild>
    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono-ticker uppercase tracking-widest text-emerald-300 cursor-help" data-testid={tid}>
      <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" /></span>
      Live · {n}
    </span>
  </Hint>
) : null;

const LiveCell = ({ live, fallback, change, tid }) => {
  const [flash, setFlash] = useState(null);
  const prev = useRef(live?.price);
  useEffect(() => {
    if (live?.price == null || prev.current == null || live.price === prev.current) { prev.current = live?.price ?? prev.current; return undefined; }
    setFlash(live.price > prev.current ? "up" : "down");
    prev.current = live.price;
    const id = setTimeout(() => setFlash(null), 500);
    return () => clearTimeout(id);
  }, [live?.price]);
  const price = live?.price ?? (typeof fallback === "number" ? fallback : null);
  const ch = live?.change24 ?? change;
  if (price == null) return null;
  return (
    <div className="text-right shrink-0" data-testid={tid}>
      <div className={`font-mono-ticker text-xs transition-colors duration-300 ${flash === "up" ? "text-emerald-300" : flash === "down" ? "text-red-300" : "text-slate-200"}`}>
        {fmtUsd(price)}{live && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 ml-1 align-middle animate-pulse" />}
      </div>
      {ch != null && <div className={`font-mono-ticker text-[10px] ${ch >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtPct(ch)}</div>}
    </div>
  );
};

const SentimentDial = ({ fng }) => {
  if (!fng) return null;
  const v = Number(fng.value || 0);
  const label = fng.value_classification;
  const deg = (v / 100) * 180 - 90;
  const color = v < 25 ? "#EF4444" : v < 45 ? "#F59E0B" : v < 55 ? "#eab308" : v < 75 ? "#10B981" : "#06B6D4";
  return (
    <div className="apx-card p-5 rise rise-d1" data-testid="fear-greed-widget">
      <div className="flex items-center gap-2 mb-3">
        <Gauge className="w-4 h-4 text-cyan-400" />
        <div className="text-xs font-mono-ticker uppercase tracking-widest text-slate-400"><HintLabel k="fear_greed">Fear & Greed</HintLabel></div>
      </div>
      <div className="relative h-28 flex items-end justify-center">
        <svg viewBox="0 0 200 110" width="100%" height="100%">
          <defs>
            <linearGradient id="dial" x1="0" x2="1">
              <stop offset="0" stopColor="#EF4444" />
              <stop offset="0.5" stopColor="#F59E0B" />
              <stop offset="1" stopColor="#06B6D4" />
            </linearGradient>
          </defs>
          <path d="M 10 100 A 90 90 0 0 1 190 100" stroke="url(#dial)" strokeWidth="14" fill="none" strokeLinecap="round" />
          <g transform={`translate(100 100) rotate(${deg})`}>
            <line x1="0" y1="0" x2="0" y2="-72" stroke="#F8FAFC" strokeWidth="3" strokeLinecap="round" />
            <circle r="6" fill={color} />
          </g>
        </svg>
      </div>
      <div className="text-center">
        <div className="font-mono-ticker text-3xl font-bold" style={{ color }}>{v}</div>
        <div className="text-xs text-slate-400">{label}</div>
      </div>
    </div>
  );
};

const MoversUnavailable = ({ tid }) => (
  <div className="p-6 text-center text-xs text-slate-500" data-testid={tid}>Unavailable — market-data refresh is paused by a provider limit; movers return with the next successful snapshot.</div>
);

export default function Dashboard() {
  const [coins, setCoins] = useState([]);
  const [movers, setMovers] = useState({ gainers: [], losers: [] });
  const [global, setGlobal] = useState(null);
  const [fng, setFng] = useState(null);
  const [trending, setTrending] = useState([]);
  const [watch, setWatch] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [marketError, setMarketError] = useState(null); // { rateLimited, until } when the market table could not be refreshed
  const backoffRef = useRef(0); // read inside the interval (state would be a stale closure)
  const retryTimer = useRef(null);

  const loadWatch = async () => {
    try {
      const r = await api.get("/watchlist");
      setWatch(r.data || []);
    } catch (e) { console.warn("[apex] ignored:", e); }
  };

  const refresh = async () => {
    if (Date.now() < backoffRef.current) return; // respect the provider's back-off window
    try {
      const [m, g, mv, tr] = await Promise.all([
        api.get("/market/top", { params: { per_page: 50 } }),
        api.get("/market/global").catch(() => null),
        api.get("/market/movers").catch(() => null),
        api.get("/market/trending").catch(() => null),
      ]);
      setCoins(m.data);
      setMarketError(null);
      if (g) { setGlobal(g.data.global); setFng(g.data.fear_greed); }
      if (mv) setMovers(mv.data);
      if (tr) setTrending((tr.data?.coins || []).slice(0, 8).map((x) => x.item));
    } catch (e) {
      const rateLimited = e?.response?.status === 503;
      const ra = e?.retryAfter || retryAfterOf(e) || (rateLimited ? 60 : 20);
      backoffRef.current = Date.now() + ra * 1000;
      setMarketError({ rateLimited, until: backoffRef.current });
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(refresh, ra * 1000 + 500); // one retry right after the window, then the normal interval
      console.warn("[apex] market refresh failed:", e?.message || e, `(retry in ${ra}s)`);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    loadWatch();
    const i = setInterval(refresh, 45000);
    return () => { clearInterval(i); clearTimeout(retryTimer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const watchIds = new Set(watch.map((w) => w.id));
  const livePrices = useLivePrices([
    ...watch.map((w) => ({ symbol: w.symbol, id: w.id })),
    ...movers.gainers.slice(0, 5).map((c) => ({ symbol: c.symbol, id: c.id })),
    ...movers.losers.slice(0, 5).map((c) => ({ symbol: c.symbol, id: c.id })),
    ...trending.map((t) => ({ symbol: t.symbol, id: t.id })),
    ...coins.map((c) => ({ symbol: c.symbol, id: c.id })),
  ]);
  const lp = (sym) => livePrices[(sym || "").toUpperCase()];
  const liveRef = useRef(livePrices);
  liveRef.current = livePrices; // the ticker polls this every second instead of re-rendering on every live flush
  const liveCount = watch.filter((w) => lp(w.symbol)?.state === "live").length;
  const countLive = (list) => list.filter((c) => lp(c.symbol)?.state === "live").length;

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Hero stats */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Market Overview</h1>
          <p className="text-sm text-slate-400 mt-1">Live prices · AI-powered research · Paper trading simulator</p>
        </div>
        <div className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500 flex items-center gap-2">
          <Globe className="w-3.5 h-3.5" /> Powered by CoinGecko
        </div>
      </div>

      <DashboardGauges />

      <PriceTicker coins={coins} liveRef={liveRef} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat
          k="total_market_cap"
          label="Total Market Cap"
          value={global ? `$${fmtNum(global.total_market_cap?.usd)}` : "—"}
          hint={global ? `${fmtPct(global.market_cap_change_percentage_24h_usd)} 24h` : ""}
          accent={global?.market_cap_change_percentage_24h_usd >= 0 ? "text-emerald-400" : "text-red-400"}
        />
        <Stat k="global_volume" label="24h Volume" value={global ? `$${fmtNum(global.total_volume?.usd)}` : "—"} />
        <Stat
          k="btc_dominance"
          label="BTC Dominance"
          value={global?.market_cap_percentage?.btc ? `${global.market_cap_percentage.btc.toFixed(2)}%` : "—"}
        />
        <Stat
          k="active_coins"
          label="Active Coins"
          value={global?.active_cryptocurrencies ? fmtNum(global.active_cryptocurrencies, 0) : "—"}
        />
      </div>

      {/* Row: Watchlist + Sentiment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 apx-card rise rise-d2">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400" />
              <Hint k="watchlist_card" side="right"><h3 className="font-display text-base font-semibold cursor-help">Your Watchlist</h3></Hint>
            </div>
            <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500 inline-flex items-center gap-2">
              {liveCount > 0 && (
                <Hint title="Live prices" text="Watchlist prices tick in real time from the EV-IDEX market feed — no refresh needed." asChild>
                  <span className="inline-flex items-center gap-1 text-emerald-300 cursor-help" data-testid="watchlist-live-badge">
                    <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" /></span>
                    Live · {liveCount}
                  </span>
                </Hint>
              )}
              {watch.length} coins
            </span>
          </div>
          {watch.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="empty-watchlist">
              Star any coin to add it to your watchlist.
            </div>
          ) : (
            <div className="divide-y divide-slate-900">
              {watch.map((c) => (
                <CoinRow key={c.id} coin={c} inWatchlist onWatchChange={loadWatch} live={livePrices[(c.symbol || "").toUpperCase()]} />
              ))}
            </div>
          )}
        </div>
        <SentimentDial fng={fng} />
      </div>

      {/* Row: Gainers & Losers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="apx-card rise rise-d3" data-testid="gainers-card">
          <div className="flex items-center gap-2 p-4 border-b border-slate-800">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <Hint k="gainers" side="right"><h3 className="font-display text-base font-semibold cursor-help">Top Gainers · 24h</h3></Hint>
            <LiveBadge n={countLive(movers.gainers.slice(0, 5))} tid="gainers-live-badge" />
          </div>
          <div className="divide-y divide-slate-900">
            {marketError && !movers.gainers.length && <MoversUnavailable tid="gainers-unavailable" />}
            {movers.gainers.slice(0, 5).map((c) => (
              <CoinRow key={c.id} coin={c} inWatchlist={watchIds.has(c.id)} onWatchChange={loadWatch} live={lp(c.symbol)} />
            ))}
          </div>
        </div>
        <div className="apx-card rise rise-d3" data-testid="losers-card">
          <div className="flex items-center gap-2 p-4 border-b border-slate-800">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <Hint k="losers" side="right"><h3 className="font-display text-base font-semibold cursor-help">Top Losers · 24h</h3></Hint>
            <LiveBadge n={countLive(movers.losers.slice(0, 5))} tid="losers-live-badge" />
          </div>
          <div className="divide-y divide-slate-900">
            {marketError && !movers.losers.length && <MoversUnavailable tid="losers-unavailable" />}
            {movers.losers.slice(0, 5).map((c) => (
              <CoinRow key={c.id} coin={c} inWatchlist={watchIds.has(c.id)} onWatchChange={loadWatch} live={lp(c.symbol)} />
            ))}
          </div>
        </div>
      </div>

      {/* Trending */}
      {trending.length > 0 && (
        <div className="apx-card rise rise-d4" data-testid="trending-card">
          <div className="flex items-center gap-2 p-4 border-b border-slate-800">
            <Flame className="w-4 h-4 text-amber-400" />
            <Hint k="trending" side="right"><h3 className="font-display text-base font-semibold cursor-help">Trending</h3></Hint>
            <LiveBadge n={countLive(trending)} tid="trending-live-badge" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {trending.map((t) => (
              <button
                key={t.id}
                data-testid={`trend-${t.id}`}
                onClick={() => navigate(`/coin/${t.id}`)}
                className="flex items-center gap-3 p-3 hover:bg-slate-800/40 border-r border-b border-slate-900 text-left"
              >
                <img src={t.small} alt="" className="w-7 h-7 rounded-full" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-[10px] font-mono-ticker uppercase text-slate-500">{t.symbol}</div>
                </div>
                <LiveCell live={lp(t.symbol)} fallback={t.data?.price} change={t.data?.price_change_percentage_24h?.usd} tid={`trend-live-${t.id}`} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Top table */}
      <div className="apx-card">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="font-display text-base font-semibold">Top Cryptocurrencies</h3>
          <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">
            By market cap
          </span>
          <LiveBadge n={countLive(coins)} tid="top-live-badge" />
        </div>
        <div className="hidden lg:grid grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_auto_auto] gap-3 px-4 py-2 border-b border-slate-900 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">
          <span>#</span><span>Name</span><span className="text-right"><Term k="price_live_snapshot" side="bottom" iconClass="normal-case">Price</Term></span>
          <span className="text-right"><Term k="price_change_24h" side="bottom" iconClass="normal-case">24h</Term></span><span className="text-right"><Term k="market_cap" side="bottom" iconClass="normal-case">Mkt Cap</Term></span>
          <span className="text-right"><Term k="spark_7d" side="bottom" iconClass="normal-case">7d</Term></span><span></span>
        </div>
        <div className="divide-y divide-slate-900" data-testid="top-coins-list">
          {marketError && (
            <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-amber-950/30 text-xs" data-testid="market-refresh-notice" data-rate-limited={marketError.rateLimited ? "1" : "0"}>
              <span className="font-semibold tracking-widest text-amber-300">{coins.length ? "DELAYED" : "UNAVAILABLE"}</span>
              <span className="text-amber-100/90">
                {marketError.rateLimited ? "Market-data refresh is paused by a provider limit — " : "Market-data refresh failed — "}
                {coins.length ? "showing the last snapshot received; each row shows its own update time." : "no snapshot is available yet, so no prices are shown."}
              </span>
              <RetryButton until={marketError.until} onRetry={() => { backoffRef.current = 0; refresh(); }} tid="market-refresh-retry" className="ml-auto !px-3 !py-1" />
            </div>
          )}
          {loading && Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-14 m-3 skeleton" />)}
          {!loading && coins.map((c) => (
            <CoinRow key={c.id} coin={c} inWatchlist={watchIds.has(c.id)} onWatchChange={loadWatch} live={lp(c.symbol)} />
          ))}
        </div>
      </div>
    </div>
  );
}
